import { Button } from '@saay/shadcn/components/button';
import { ToggleGroup, ToggleGroupItem } from '@saay/shadcn/components/toggle-group';
import { cn } from '@saay/shadcn/lib/utils';
import type { LucideIcon } from 'lucide-react';
import {
  cloneElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useScrollEdges } from './scroll-edges.js';
import { IconTile, TONE_STRONG, type Tone } from './tone.js';

const TOP_BAR_SCROLL_THRESHOLD = 8;

/** Hides a floating top bar after a deliberate downward scroll and restores it on the way up. */
export function useTopBarVisibility(): boolean {
  const [hidden, setHidden] = useState(false);
  const previousScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      const delta = currentScrollY - previousScrollY.current;
      if (currentScrollY <= TOP_BAR_SCROLL_THRESHOLD) {
        previousScrollY.current = currentScrollY;
        setHidden(false);
        return;
      }
      if (Math.abs(delta) < TOP_BAR_SCROLL_THRESHOLD) return;
      previousScrollY.current = currentScrollY;
      setHidden(delta > 0);
    };

    previousScrollY.current = Math.max(window.scrollY, 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return hidden;
}

/** The service grid: four per row, a large tinted tile with the label under it. */
export function QuickActions({
  items,
}: {
  readonly items: readonly {
    readonly label: string;
    readonly icon: LucideIcon;
    readonly tone: Tone;
    /** A router link (e.g. `<Link to="/qr" />`); a button otherwise. */
    readonly render?: ReactElement;
    readonly onClick?: () => void;
  }[];
}) {
  return (
    <nav className="grid grid-cols-4 gap-2">
      {items.map((item) => (
        <Button
          className="h-auto flex-col gap-2 rounded-2xl px-0 py-1 text-xs font-medium whitespace-nowrap hover:bg-transparent"
          key={item.label}
          variant="ghost"
          {...(item.render === undefined ? {} : { render: item.render, nativeButton: false })}
          {...(item.onClick === undefined ? {} : { onClick: item.onClick })}
        >
          <IconTile icon={item.icon} size="lg" tone={item.tone} />
          {item.label}
        </Button>
      ))}
    </nav>
  );
}

/** One week of the strip or the slider: what its cell shows and its accessible name. */
export type WeekCellData = {
  readonly id: string;
  readonly date: string;
  readonly amount: ReactNode;
  readonly unit?: string;
  readonly tone: Tone | 'none';
  readonly label: string;
};

/** The cell body shared by `WeekStrip` and `WeekSlider`: date, amount (and unit), a tone line. */
function WeekCellBody({ week }: { readonly week: WeekCellData }) {
  return (
    <>
      <span className="text-[11px] font-normal text-muted-foreground">{week.date}</span>
      <span className="mt-1 text-sm font-bold tabular-nums">{week.amount}</span>
      {week.unit === undefined ? null : (
        <span className="text-[10px] font-normal text-muted-foreground">{week.unit}</span>
      )}
      <span
        className={cn(
          'mt-1.5 block h-1 w-6 rounded-full',
          week.tone === 'none' ? 'bg-border' : TONE_STRONG[week.tone],
        )}
      />
    </>
  );
}

/**
 * The weeks of installments as a strip (a ToggleGroup of four columns): date, amount, and a tone
 * line; the selected week is raised. Selecting a week is optional (`onSelect`). For every week in
 * one scrolling row where a week opens a page, use `WeekSlider`.
 */
export function WeekStrip({
  weeks,
  value,
  onSelect,
}: {
  readonly weeks: readonly WeekCellData[];
  readonly value: string;
  readonly onSelect?: (id: string) => void;
}) {
  return (
    <ToggleGroup
      className="grid w-full grid-cols-4 gap-2"
      onValueChange={(next) => {
        const id = next[0];
        if (id !== undefined && onSelect !== undefined) onSelect(String(id));
      }}
      spacing={2}
      value={[value]}
    >
      {weeks.map((week) => (
        <ToggleGroupItem
          aria-label={week.label}
          className="h-auto flex-col gap-0 rounded-2xl px-2 py-2.5 data-pressed:bg-background data-pressed:shadow-sm"
          key={week.id}
          value={week.id}
        >
          <WeekCellBody week={week} />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** The props a week's link element accepts from the slider (a router `<Link to=… />` or `<a>`). */
type WeekLinkProps = {
  readonly className?: string;
  readonly children?: ReactNode;
  readonly 'aria-label'?: string;
  readonly 'aria-current'?: 'date';
  readonly 'data-week'?: string;
};

/**
 * The slider variant of the week strip (owner, 2026-10-22): every week in one horizontally
 * scrolling row that snaps cell by cell, about four cells on a phone, no visible scrollbar, and the
 * reading direction's first week at the start. Each cell is a link (`render`, e.g.
 * `<Link to="/installments?week=…" />`), so it is reached with Tab like any link and focusing it
 * scrolls it into view. The `current` week is raised, marked `aria-current="date"`, and scrolled to
 * the start of the row once when it first appears; later refetches never move the buyer's scroll.
 */
export function WeekSlider({
  weeks,
  current,
  label,
  className,
}: {
  readonly weeks: readonly (WeekCellData & {
    /** The link this week opens; the slider adds the cell's content, class, and labels. */
    readonly render: ReactElement<WeekLinkProps>;
  })[];
  readonly current?: string;
  readonly label: string;
  readonly className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(ref);
  const hasCurrent = current !== undefined && weeks.some((week) => week.id === current);

  useEffect(() => {
    const slider = ref.current;
    if (slider === null || !hasCurrent) {
      return;
    }
    const cell = [...slider.querySelectorAll<HTMLElement>('[data-week]')].find(
      (element) => element.dataset['week'] === current,
    );
    if (cell === undefined) {
      return;
    }
    // Physical deltas: in RTL the start edge is the right one, so align the cell's right edge.
    const rtl = getComputedStyle(slider).direction === 'rtl';
    const box = slider.getBoundingClientRect();
    const target = cell.getBoundingClientRect();
    const delta = rtl ? target.right - box.right : target.left - box.left;
    if (delta !== 0) {
      slider.scrollLeft += delta;
    }
    // Only when the current week changes (a new week), never on a refetch of the same list.
  }, [current, hasCurrent]);

  return (
    <div
      aria-label={label}
      className={cn(
        'week-slider -mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain px-1 py-1',
        className,
      )}
      data-fade-end={edges.end ? '' : undefined}
      data-fade-start={edges.start ? '' : undefined}
      ref={ref}
      role="list"
    >
      {weeks.map((week) => {
        const isCurrent = week.id === current;
        return (
          <div className="w-[4.5rem] shrink-0 snap-start" key={week.id} role="listitem">
            {cloneElement(week.render, {
              'aria-label': week.label,
              ...(isCurrent ? { 'aria-current': 'date' as const } : {}),
              children: <WeekCellBody week={week} />,
              className: cn(
                'flex h-full flex-col items-center rounded-2xl px-1.5 py-2.5 text-center text-foreground no-underline outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50',
                isCurrent ? 'bg-background shadow-sm hover:bg-background' : '',
                week.render.props.className,
              ),
              'data-week': week.id,
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The buyer's bottom bar: the app's own items with the raised round centre action (QR خرید).
 * Items are Buttons; pass `render` with a router link for navigation.
 */
export function BottomNav({
  items,
  center,
  className,
}: {
  readonly items: readonly {
    readonly label: string;
    readonly icon: LucideIcon;
    readonly active?: boolean;
    readonly render?: ReactElement;
  }[];
  readonly center: {
    readonly label: string;
    readonly icon: LucideIcon;
    readonly render?: ReactElement;
  };
  readonly className?: string;
}) {
  const half = Math.ceil(items.length / 2);
  const item = (entry: (typeof items)[number]) => (
    <Button
      aria-current={entry.active === true ? 'page' : undefined}
      className={cn(
        'relative h-full flex-1 flex-col gap-0.5 rounded-[24px] text-[11px] font-medium hover:bg-transparent [&_svg]:size-[22px]!',
        entry.active === true ? 'text-primary hover:text-primary' : 'text-muted-foreground',
      )}
      key={entry.label}
      variant="ghost"
      {...(entry.render === undefined ? {} : { render: entry.render, nativeButton: false })}
    >
      {/* The active item's icon sits on a soft primary pill (Material 3 navigation indicator). */}
      <span
        className={cn(
          'grid h-8 w-14 place-items-center rounded-full transition-colors',
          entry.active === true ? 'bg-primary/8' : '',
        )}
      >
        <entry.icon />
      </span>
      {entry.label}
    </Button>
  );
  return (
    <nav
      className={cn(
        // Floating glass bar: rounded, a small gap from the screen edges and the safe area.
        'fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[420px] px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]',
        className,
      )}
    >
      <div className="glass flex h-[60px] items-stretch rounded-[24px]">
        {items.slice(0, half).map(item)}
        <div className="flex flex-1 justify-center">
          <Button
            aria-label={center.label}
            className="-mt-4 size-14 rounded-full shadow-[0_6px_14px_-8px_var(--glass-shadow)] ring-4 ring-background [&_svg]:size-6!"
            size="icon"
            {...(center.render === undefined ? {} : { render: center.render, nativeButton: false })}
          >
            <center.icon />
          </Button>
        </div>
        {items.slice(half).map(item)}
      </div>
    </nav>
  );
}

/**
 * The buyer's top app bar: a floating glass bar under the status bar, rounded, with a small gap from
 * the screen edges; the content scrolls under it.
 */
export function AppBar({
  as: Element = 'header',
  children,
  className,
}: {
  /** `div` for a second bar on a page that already has its banner (the desktop subpage bar). */
  readonly as?: 'header' | 'div';
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const hidden = useTopBarVisibility();
  return (
    <Element
      className={cn(
        'sticky top-0 z-30 px-3 pt-[max(env(safe-area-inset-top),0.5rem)] transition-[translate,transform,opacity] duration-[250ms] ease-out motion-reduce:transition-none',
        // Owner (2026-10-28): hiding is the exact reverse of showing. Showing slides the bar down
        // first and then widens it; hiding narrows it first and then slides it up.
        hidden && '-translate-y-[calc(100%+0.5rem)] opacity-0 delay-[200ms]',
        className,
      )}
      data-topbar-hidden={hidden ? '' : undefined}
    >
      <div
        className={cn(
          'glass mx-auto flex h-[52px] items-center justify-between gap-3 rounded-[20px] transition-[width] duration-[250ms] ease-out motion-reduce:transition-none ps-2 pe-1.5',
          hidden ? 'w-4/5' : 'w-full delay-[200ms]',
        )}
      >
        {children}
      </div>
    </Element>
  );
}
